// @ts-nocheck
/**
 * Component props
 */
const props = {
  /**
   * prop: 'media'
   * MediaStream object for visualisation. Can be delivered by
   * Web Audio API functions like getUserMedia or RTCPeerConnection
   */
  media: {
    type: MediaStream,
    required: false,
    default: null,
  },

  /**
   * prop: 'canv-width'
   * Canvas element width. Default 300
   */
  canvWidth: {
    type: Number,
    default: 300,
  },

  /**
   * prop: 'canv-class'
   * Canvas element css class name.
   */
  canvClass: {
    type: String,
    default: null,
  },

  /**
   * prop: 'canv-height'
   * Canvas element height. Default 80
   */
  canvHeight: {
    type: Number,
    default: 80,
  },

  circleMin: {
    type: Number,
    default: 120,
  },

  /**
   * prop: 'canv-fill-color'
   * Canvas fill background RGB color.
   * Default is transperent.
   */
  canvFillColor: {
    type: String,
    default: null,
  },

  /**
   * prop: 'fft-size'
   * Represents the window size in samples that is used when performing
   * a Fast Fourier Transform (FFT) to get frequency domain data.
   * Must be power of 2 between 2^5 and 2^15
   * Default: 8192 for 'wform' 1024 for 'freq'
   */
  fftSize: {
    type: Number,
    default: null, // 1024 // 8192
  },

  /**
   * prop: 'type'
   * Type of visualisation.
   * wform - using byte time domaine data
   * frequ - using byte frequency data
   * wform when not recognized.
   * circele
   * Default: wform
   */
  type: {
    type: String,
    default: 'circle',
  },

  /**
   * prop: 'frequ-lnum'
   * Vertical lines number for frequ type.
   * Default: 60
   */
  frequLnum: {
    type: Number,
    default: 60,
  },

  /**
   * prop: 'frequ-line-cap'
   * Draw line with rounded end caps.
   * Default: false
   */
  frequLineCap: {
    type: Boolean,
    default: false,
  },

  /**
   * prop: 'line-color'
   * Line color.
   * Default: lime
   */
  lineColor: {
    type: String,
    default: 'lime',
  },

  /**
   * prop: 'line-width'
   * Line width.
   * Default: 0.5 for wform and 3 for frequ
   */
  lineWidth: {
    type: Number,
    default: null,
  },
};

/**
 * Component AvMedia
 */
const AvMedia = {
  name: 'av-media',
  data() {
    return {
      ctx: null as CanvasRenderingContext2D | null,
      audioCtx: null as AudioContext | null,
      analyser: null as AnalyserNode | null,
    };
  },
  props,
  render: (h) => h('div'),
  mounted() {
    this.createCanvas();
  },
  watch: {
    media: function (newVal) {
      if (newVal) this.setAnalyser();
      this.draw();
    },
  },
  methods: {
    rerender: function () {
      if (this.media) this.setAnalyser();
      this.draw();
    },
    /**
     * Create Canvas inside div
     */
    createCanvas: function () {
      const canv = document.createElement('canvas');
      canv.width = this.canvWidth;
      canv.height = this.canvHeight;
      if (this.canvClass) canv.setAttribute('class', this.canvClass);
      this.ctx = canv.getContext('2d');
      this.$el.appendChild(canv);
    },

    /**
     * Set analyser
     */
    setAnalyser: function () {
      this.audioCtx = new AudioContext();
      const gainNode = this.audioCtx.createGain();
      gainNode.gain.setValueAtTime(-1, this.audioCtx.currentTime);
      this.analyser = this.audioCtx.createAnalyser();
      if (!this.media) {
        console.log('No media');
        return;
      }
      const src = this.audioCtx.createMediaStreamSource(this.media);

      src.connect(this.analyser);
      src.connect(gainNode);
      if (this.fftSize) {
        this.analyser.fftSize = this.fftSize;
      } else {
        this.analyser.fftSize = this.type === 'frequ' || this.type === 'circle' ? 1024 : 8192;
      }
      gainNode.connect(this.audioCtx.destination);
      this.analyser.connect(this.audioCtx.destination);
    },

    draw: function () {
      if (this.analyser) {
        const data = new Uint8Array(this.analyser.fftSize);

        if (this.ctx) {
          if (this.canvFillColor) this.ctx.fillStyle = this.canvFillColor;
          this.ctx.clearRect(0, 0, this.canvWidth, this.canvHeight);
          this.ctx.beginPath();
          this.ctx.strokeStyle = this.lineColor;
        }

        if (this.type === 'frequ') {
          this.analyser.getByteFrequencyData(data);
          this.frequ(data);
        } else if (this.type === 'circle') {
          this.analyser.getByteFrequencyData(data);
          this.circle(data);
        } else {
          this.analyser.getByteTimeDomainData(data);
          this.wform(data);
        }

        requestAnimationFrame(this.draw);
      }
    },

    circle: function (data) {
      if (this.ctx) {
        const minRadius = this.circleMin;
        const maxRadius = this.canvHeight;
        const value = data.reduce((sum, v) => sum + v / 255.0, 0) / 2;
        let radius = value + minRadius;
        radius = radius > minRadius ? radius : minRadius;
        radius = radius > maxRadius ? maxRadius - 1 : radius;

        this.ctx.beginPath();
        this.ctx.arc(this.canvHeight / 2, this.canvHeight / 2, radius / 2, 0, 2 * Math.PI);
        this.ctx.fillStyle = this.canvFillColor;
        this.ctx.fill();
      }
    },

    wform: function (data) {
      if (this.ctx && this.analyser) {
        const h = this.canvHeight;
        const step = this.canvWidth / this.analyser.fftSize;
        let x = 0;
        this.ctx.lineWidth = this.lineWidth || 0.5;
        data.forEach((v) => {
          const y = (v / 255.0) * h;
          this.ctx?.lineTo(x, y);
          x += step;
        });
        this.ctx.stroke();
      }
    },

    frequ: function (data) {
      if (this.ctx) {
        const c = this.frequLnum;
        const step = this.canvWidth / c;
        const h = this.canvHeight;
        const lw = this.lineWidth || 2;
        for (let i = 0; i < c; i++) {
          const x = i * step + lw;
          const v = data.slice(x, x + step).reduce((sum, v) => sum + (v / 255.0) * h, 0) / step;
          const space = (h - v) / 2 + 2; // + 2 is space for caps
          this.ctx.lineWidth = lw;
          this.ctx.lineCap = this.frequLineCap ? 'round' : 'butt';
          this.ctx.moveTo(x, space);
          this.ctx.lineTo(x, h - space);
          this.ctx.stroke();
        }
      }
    },
  },
};

export default AvMedia;
